/**
 * TUYẾN CHIẾU SÁNG — thêm · sửa · xoá, và gán tủ chưa có đường.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🔴 HAI LUẬT CHI PHỐI CẢ TRANG NÀY (chốt YC-21). Đọc trước khi sửa bất cứ dòng nào.
 *
 * 1. TÊN ĐƯỜNG KHÔNG PHẢI DANH TÍNH. "Nguyễn Huệ" P. Bến Nghé và "Nguyễn Huệ" P. Thủ Đức là HAI
 *    tuyến chiếu sáng. Nên KHÔNG CHỖ NÀO trong trang được hiện tên đường một mình — danh sách,
 *    ô lọc, tiêu đề, và nhất là hộp thoại xoá. Một hộp thoại *"Xoá đường Nguyễn Huệ?"* là
 *    hộp thoại KHÔNG TRẢ LỜI ĐƯỢC, và giá của một cú bấm nhầm là một tuyến đường tối.
 *
 *    ⇒ Chuỗi hiển thị lấy từ `tenDayDu` do MÁY CHỦ gửi xuống. Trang này KHÔNG tự ghép
 *      tên + địa chỉ: hai chỗ ghép là hai chỗ sẽ trôi khỏi nhau.
 *
 * 2. `routeId` CHO PHÉP RỖNG. Tủ vừa cắm chưa có đường, tủ vừa bị gỡ cũng vậy. Khối
 *    "Danh sách chờ" ở cuối trang là chỗ DUY NHẤT chúng hiện ra — cây tài sản
 *    duyệt theo tuyến chiếu sáng nên không có đường là không có chỗ đứng.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, ArrowUpDown, Ban, ChevronDown, ChevronRight, ClipboardList, Download, MapPin,
  Pencil, Plus, Route as RouteIcon, Server, ShieldCheck, Trash2, Unplug,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { qk, useBiChan, useChoDuyet, useDanhSachCho, useDemTuTimThay, useRoutes, useTree } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { useNemaBat } from '../lib/nema';
import { useCayCaiDat } from '../lib/caiDatBanDau';
import {
  Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBox, Field, Modal, OSoLon, Spinner, StatusDot, useToast,
  LOP_CHUA_RO,
} from '../components/ui';
import { fmtAgo, fmtDateTime } from '../lib/format';
import {
  NHAN_THIEU, type RouteRow, type Thieu, type TreeCabinet, type TreeRoute, type TuChoDuyet, type TuChoXuLy,
} from '../lib/types';
import { ghepCsv, oAnToan, oVanBan, taiVe, tenTepCsv } from '../lib/csv';
import { khoaTim } from '../lib/khongdau';
import { useDatDoiTuong } from '../lib/doiTuongDangChon';

/** Khớp tìm kiếm trên CẢ tên LẪN địa chỉ — gõ "Thủ Đức" phải ra được. */
function khop(r: RouteRow, kw: string) {
  const k = kw.trim().toLowerCase();
  if (!k) return true;
  return `${r.name} ${r.phuong ?? ''} ${r.tinh ?? ''} ${r.code ?? ''}`
    .toLowerCase().includes(k);
}

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-06` 11/09/2026 — QUẢN LÝ TUYẾN v2 (mẫu `_BUS/26`).
 *
 * 🔴 ĐỔI GÌ VỀ Ý, không chỉ về hình: bản cũ là một DANH SÁCH THU-MỞ, và một danh sách thu-mở
 *   trả lời được đúng một câu — *"tuyến này có những tủ nào"*. Câu mà người trực hỏi mỗi sáng
 *   thì nó KHÔNG trả lời được: *"tuyến nào đang có tủ mất kết nối"*. Muốn biết thì phải mở
 *   từng tuyến một, và mở tuyến thứ tư là quên tuyến thứ nhất.
 * ⇒ Bảng có cột, sắp xếp được. Một cột "k/n nối" đọc được cả trang bằng một lần quét mắt, và
 *   sắp xếp theo cột ấy đưa tuyến hỏng lên đầu.
 *
 * ⛔ HAI LUẬT YC-21 Ở ĐẦU TỆP KHÔNG ĐỔI MỘT CHỮ, và bảng làm chúng KHÓ HƠN chứ không dễ hơn:
 *   một cái bảng có cột "Tên tuyến" là đúng cái chỗ mà tên đường dễ đứng một mình nhất.
 *   Cách giữ luật ở đây, nói ra để không ai "dọn" mất:
 *     · cột "Tên tuyến" và cột "Địa chỉ" NẰM CẠNH NHAU, không cột nào ẩn được;
 *     · tuyến KHÔNG có địa chỉ thì ô địa chỉ nói ra bằng chữ hổ phách — đó CHÍNH LÀ ca mà
 *       tên đứng một mình, và nó phải kêu lên chứ không được để trống;
 *     · MỌI chỗ nguy hiểm (hộp xoá · ô chọn tuyến · thanh hàng loạt · lời báo) vẫn dùng
 *       `tenDayDu` do MÁY CHỦ dựng, không dùng cặp hai cột.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/** Cột đang sắp xếp. `null` = giữ thứ tự máy chủ trả về (`orderNo`). */
type CotXep = 'ten' | 'diaChi' | 'soTu' | 'noi';

/**
 * ⛔ BỘ GHÉP ĐỊA CHỈ **DUY NHẤT** CỦA TRANG — và nó KHÔNG phải cái bị YC-21 cấm.
 *   Cái bị cấm là ghép **tên + địa chỉ** thành một DANH TÍNH: danh tính do máy chủ dựng
 *   (`tenDayDu`), vì hai chỗ ghép danh tính là hai chỗ sẽ trôi khỏi nhau.
 *   Hàm này chỉ dựng phần ĐỊA CHỈ cho một CỘT — nó không thay thế danh tính ở bất cứ đâu.
 * ⚠ TK-21: HAI cấp — Xã/Phường, Tỉnh/Thành phố. Không còn cấp huyện (cột cũ đóng băng ở máy chủ, không ra API).
 * 🔴 TRẢ `null` KHI KHÔNG CÓ MẢNH NÀO (Luật số 0) — chỗ gọi phải nói ra, không được để trống.
 */
function diaChi(r: RouteRow): string | null {
  const manh = [r.phuong, r.tinh].map((x) => x?.trim()).filter(Boolean);
  return manh.length ? manh.join(', ') : null;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TRẠNG THÁI NỐI CỦA MỘT TUYẾN — và đây là chỗ Luật số 0 đắt nhất của trang.
 *
 * 🔴 `0/3 nối` và `chưa đo được` TRÔNG GẦN GIỐNG NHAU trên màn hình và có nghĩa NGƯỢC NHAU:
 *   cái đầu là *"ba cái tủ ngoài kia đang mất kết nối"* — một sự cố phải đi xử lý ngay; cái
 *   sau là *"tôi chưa hỏi được máy chủ"*. Vẽ cái sau thành cái trước là dựng một sự cố không
 *   có thật, và người trực sẽ chạy ra hiện trường.
 *
 * ⛔ MỘT NGUỒN: `useTree()` trả CẢ `cabinetCount` LẪN `onlineCount`, do máy chủ đếm. Trang này
 *   KHÔNG tự đếm `cabinets.filter(status === 'ONLINE')` — đếm lại là dựng bản luật thứ hai,
 *   và nó sẽ lệch vào đúng ngày máy chủ đổi định nghĩa "online".
 * ⚠ `_count.cabinets` của `/api/routes` chỉ dùng cho MỘT ca: tuyến không có trong cây. Khi ấy
 *   `n` biết mà `k` không biết ⇒ vẫn là "chưa đo được", không phải `0/n`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
type MucNoi = 'du' | 'mot-phan' | 'mat-het' | 'chua-co-tu' | 'chua-do-duoc';
type TrangThaiNoi = { muc: MucNoi; k: number | null; n: number | null };

function docNoi(r: RouteRow, cayData: TreeRoute[] | undefined): TrangThaiNoi {
  if (cayData === undefined) return { muc: 'chua-do-duoc', k: null, n: r._count?.cabinets ?? null };
  const t = cayData.find((x) => x.id === r.id);
  if (!t) {
    const n = r._count?.cabinets ?? null;
    /* Không có trong cây mà `_count` nói 0 tủ ⇒ "chưa có tủ" là một câu ĐO ĐƯỢC. */
    if (n === 0) return { muc: 'chua-co-tu', k: null, n: 0 };
    return { muc: 'chua-do-duoc', k: null, n };
  }
  const n = t.cabinetCount;
  const k = t.onlineCount;
  if (n === 0) return { muc: 'chua-co-tu', k: 0, n: 0 };
  if (k === 0) return { muc: 'mat-het', k, n };
  if (k >= n) return { muc: 'du', k, n };
  return { muc: 'mot-phan', k, n };
}

/**
 * ⛔ MÀU THEO LUẬT `[95]` của `thietke.css` + NGÂN SÁCH MÀU 12/10, không theo gu:
 *     lá  = đã đo và đang chạy đúng      (k = n > 0)
 *     xám = mất kết nối (một phần / hết) · đang tải / chưa đo được · "chưa có tủ"
 *   (TK-33c: bản trước tô cam "một phần" và đỏ "mất hết" — trái ngân sách màu, mất kết nối là XÁM.)
 * ⚠ "Chưa có tủ" dùng xám chứ KHÔNG dùng cam: một tuyến vừa tạo chưa gán tủ là chuyện bình
 *   thường của mười phút sau khi tạo, không phải một việc ai đó phải đi làm ngay.
 */
/*
 * TK-33c (lee 22/10): NGÂN SÁCH MÀU (luật lee 12/10, TK-30) — "Mất kết nối" là XÁM ở MỌI chỗ, kể cả chip "x/y nối" của trang
 *   này. Một phần mất hay mất hết đều là tủ mất kết nối ⇒ xám; CHỮ "1/4 nối" / "0/2 nối" nói mức, không cần màu. Đỏ chỉ cho
 *   Khẩn / Không sáng / Mất điện; cam chỉ cho Cảnh báo. Lá vẫn là "đủ nối" (đã đo, đang đúng).
 */
const MAU_NOI: Record<MucNoi, 'green' | 'amber' | 'red' | 'slate'> = {
  du: 'green', 'mot-phan': 'slate', 'mat-het': 'slate', 'chua-co-tu': 'slate', 'chua-do-duoc': 'slate',
};

function chuNoi(t: TrangThaiNoi): string {
  if (t.muc === 'chua-do-duoc') return 'chưa đo được';
  if (t.muc === 'chua-co-tu') return 'chưa có tủ';
  return `${t.k}/${t.n} nối`;
}

/** Thứ hạng để SẮP XẾP theo mức hỏng — hỏng nặng lên đầu khi xếp giảm dần. */
const HANG_NOI: Record<MucNoi, number> = {
  'mat-het': 4, 'mot-phan': 3, du: 2, 'chua-co-tu': 1, 'chua-do-duoc': 0,
};

export default function ConDuongPage() {
  const routes = useRoutes();
  const cay = useTree();          // để liệt kê tủ của từng tuyến chiếu sáng
  /*
   * 🔴 10/09 `Q14` — KHỐI "DANH SÁCH CHỜ" CHỈ CHO `SUPER_ADMIN`. lee chốt: *tủ chưa phân tuyến
   *   chỉ `SUPER_ADMIN` thấy*. Máy chủ đã trả rỗng cho hạng khác, nhưng **rỗng ở đây đọc thành
   *   "không có tủ nào đang chờ"** — một câu SAI với người đọc nó. Nên không dựng khối, thay vì
   *   dựng một khối rỗng kèm chú thích: không có con số thì không có gì để đọc nhầm.
   * ⚠ Và không gọi luôn cả truy vấn: một lời gọi chắc chắn trả rỗng là một vòng mạng để lấy về
   *   một câu ta đã biết.
   * ⛔ `GD-06` — CÙNG LUẬT ẤY ÁP CHO Ô SỐ "TỦ CHỜ GÁN": không phải `SUPER_ADMIN` thì KHÔNG
   *   dựng ô ấy. Một ô số `0` cạnh ba ô số thật là một phép đo giả, và nó còn tệ hơn cái khối
   *   rỗng vì nó gọn gàng hơn — mắt tin một con số nhanh hơn tin một danh sách trống.
   */
  const laSuper = useAuth().user?.role === 'SUPER_ADMIN';
  const cho = useDanhSachCho({ enabled: laSuper });
  const toast = useToast();
  const qc = useQueryClient();

  const [kw, setKw] = useState('');
  const [locNoi, setLocNoi] = useState<MucNoi | 'tat-ca'>('tat-ca');
  const [xepTheo, setXepTheo] = useState<CotXep | null>(null);
  const [xepNguoc, setXepNguoc] = useState(false);
  const [dangSua, setDangSua] = useState<RouteRow | 'moi' | null>(null);
  const [dangXoa, setDangXoa] = useState<RouteRow | null>(null);
  const [dangGan, setDangGan] = useState<TuChoXuLy | null>(null);
  const [locThieu, setLocThieu] = useState<Thieu | 'tat-ca'>('tat-ca');
  const [timTu, setTimTu] = useState('');
  const [dangGo, setDangGo] = useState<{ tu: TreeCabinet; duong: RouteRow } | null>(null);
  const [moRong, setMoRong] = useState<string | null>(null);
  const [dangDatTen, setDangDatTen] = useState<TuChoXuLy | null>(null);
  /** Tủ đang tick ở danh sách chờ — `Set` id, KHÔNG phải mảng tủ (tủ sẽ cũ đi sau mỗi lần tải lại). */
  const [tick, setTick] = useState<Set<string>>(new Set());
  const [hangLoat, setHangLoat] = useState<'gan' | 'dat-ten' | null>(null);

  useEffect(() => { document.title = 'Quản lý tủ — SDKD'; }, []);

  const ds = useMemo(() => (routes.data ?? []).filter((r) => khop(r, kw)), [routes.data, kw]);
  /* TK-45 §2: thanh đáy — tủ đang mở trong hộp thoại (gán tuyến / đặt tên / gỡ), không thì tuyến đang mở rộng */
  useDatDoiTuong({
    tuyen: dangGo?.duong.tenDayDu ?? (moRong ? (routes.data ?? []).find((r) => r.id === moRong)?.tenDayDu : null),
    tu: dangGo?.tu.name ?? dangGan?.name ?? dangDatTen?.name ?? null,
  });

  /*
   * ⛔ LỌC RỒI MỚI XẾP, và **xếp KHÔNG sửa mảng gốc**: `ds` là kết quả `useMemo` của người khác;
   *   `.sort()` tại chỗ trên nó là sửa một thứ React đang giữ, và bảng sẽ nhảy chỗ ở lần vẽ sau
   *   mà không lần nào rõ vì sao. `[...ds]` trước khi xếp.
   * 🔴 SO CHỮ BẰNG `khoaTim` — cùng bộ bỏ dấu mà ô tìm đang dùng (`lib/khongdau.ts`). Một bảng
   *   sắp xếp bằng `localeCompare` thô sẽ xếp "Đường" sau "Zoo", và người đọc không tìm ra dòng
   *   mình vừa nhìn thấy ở ô tìm. Hai bộ so chữ là hai bộ sẽ xử sự khác nhau ở đúng tiếng Việt.
   */
  const dsXep = useMemo(() => {
    if (!xepTheo) return ds;
    const dau = xepNguoc ? -1 : 1;
    const soSanh = (a: RouteRow, b: RouteRow): number => {
      switch (xepTheo) {
        case 'ten': return khoaTim(a.tenDayDu).localeCompare(khoaTim(b.tenDayDu));
        case 'diaChi': return khoaTim(diaChi(a) ?? '').localeCompare(khoaTim(diaChi(b) ?? ''));
        case 'soTu': return (a._count?.cabinets ?? 0) - (b._count?.cabinets ?? 0);
        case 'noi': return HANG_NOI[docNoi(a, cay.data).muc] - HANG_NOI[docNoi(b, cay.data).muc];
      }
    };
    return [...ds].sort((a, b) => soSanh(a, b) * dau);
  }, [ds, xepTheo, xepNguoc, cay.data]);

  const dsHien = useMemo(
    () => (locNoi === 'tat-ca' ? dsXep : dsXep.filter((r) => docNoi(r, cay.data).muc === locNoi)),
    [dsXep, locNoi, cay.data],
  );

  const doiXep = (cot: CotXep) => {
    if (xepTheo === cot) { setXepNguoc((v) => !v); return; }
    setXepTheo(cot); setXepNguoc(false);
  };

  /*
   * ═════════════════════════════════════════════════════════════════════════════════════
   * BỐN Ô SỐ — đếm từ dữ liệu ĐANG CÓ, không một lời gọi mới nào (phiếu chốt).
   *
   * ⛔ VÀ ĐẾM TRÊN TOÀN BỘ, KHÔNG TRÊN PHẦN ĐANG LỌC: gõ một chữ vào ô tìm rồi thấy "Tổng
   *   tuyến 1" thì người đọc tưởng vừa mất ba tuyến. Cùng cái bẫy đã cắt ở `GD-05`.
   * 🔴 VÀ MỖI Ô CÓ MỘT CA "CHƯA ĐO ĐƯỢC" RIÊNG (Luật số 0): chưa tải xong `routes` thì tổng
   *   tuyến là `null`, chưa tải xong cây thì số tủ là `null`. `0` là một lời khai, và ở đây
   *   ta chưa có gì để khai.
   * ═════════════════════════════════════════════════════════════════════════════════════
   */
  const demTu = useDemTuTimThay();
  const soLieu = useMemo(() => {
    const rs = routes.data;
    const ts = cay.data;
    /*
     * ⛔ TRÙNG TÊN ĐO BẰNG `khoaTim` — không phân biệt dấu lẫn hoa thường. "Nguyễn Huệ" và
     *   "nguyen hue" là cùng một cái tên với con mắt người đọc, và chính con mắt ấy là thứ
     *   ô số này bảo vệ. So chuỗi thô thì ô số báo 0 trong khi người dùng đang nhìn hai dòng
     *   giống hệt nhau.
     * ⚠ ĐẾM SỐ **TUYẾN** nằm trong một nhóm trùng, không đếm số nhóm: hai nhóm mỗi nhóm 2
     *   tuyến thì câu trả lời đúng là "4 tuyến cần kiểm", không phải "2".
     */
    let soTrung: number | null = null;
    if (rs) {
      const dem = new Map<string, number>();
      for (const r of rs) { const k = khoaTim(r.name); dem.set(k, (dem.get(k) ?? 0) + 1); }
      soTrung = rs.filter((r) => (dem.get(khoaTim(r.name)) ?? 0) > 1).length;
    }
    const soTuTrongTuyen = ts ? ts.reduce((n, t) => n + t.cabinetCount, 0) : null;
    const soNoi = ts ? ts.reduce((n, t) => n + t.onlineCount, 0) : null;
    const cd = cho.data;
    return {
      tongTuyen: rs ? rs.length : null,
      soTuTrongTuyen,
      soNoi,
      soMat: ts && soTuTrongTuyen !== null && soNoi !== null ? soTuTrongTuyen - soNoi : null,
      soTrung,
      soCho: cd ? cd.length : null,
      /*
       * ⚠ MỘT TỦ "CẢ HAI" ĐẾM VÀO **CẢ HAI** Ô — nên hai số này cộng lại có thể LỚN HƠN tổng.
       *   Đó là câu trả lời đúng cho câu hỏi *"bao nhiêu tủ còn thiếu tên"*, và dòng phụ nói ra
       *   rằng chúng chồng nhau để không ai đi cộng hai số ấy.
       */
      chuaTen: cd ? cd.filter((t) => t.thieu === 'chua-dat-ten' || t.thieu === 'ca-hai').length : null,
      chuaGan: cd ? cd.filter((t) => t.thieu === 'chua-gan-duong' || t.thieu === 'ca-hai').length : null,
    };
  }, [routes.data, cay.data, cho.data]);

  /*
   * 🔴 MỘT NGUỒN cho cả BA thứ: danh sách hiện ra, con số trên thẻ, và tệp CSV.
   *
   * lee dặn *"xuất đúng cái đang hiện sau lọc"*. Cách chắc chắn nhất không phải là nhớ làm
   * đúng — mà là **không có cái thứ hai để lệch**: nếu CSV đi hỏi máy chủ lần nữa thì nó đã
   * là một tập khác, và không ai phát hiện ra cho tới lúc đối chiếu bằng tay.
   */
  const dsCho = useMemo(() => {
    const k = khoaTim(timTu);
    return (cho.data ?? [])
      .filter((t) => locThieu === 'tat-ca' || t.thieu === locThieu)
      .filter((t) => !k || khoaTim(`${t.name} ${t.code ?? ''} ${t.hardwareId} ${t.deviceId ?? ''}`).includes(k));
  }, [cho.data, locThieu, timTu]);

  /*
   * ⛔ TICK CHỈ TÍNH TRÊN NHỮNG TỦ ĐANG HIỆN. Một tủ được tick rồi bị bộ lọc giấu đi vẫn nằm
   *   trong `tick`, và thanh hàng loạt mà đếm nó thì người bấm sẽ gán cho một cái tủ họ KHÔNG
   *   nhìn thấy. Đó là hạng lỗi đắt nhất của một thanh hàng loạt.
   * ⚠ Giữ `tick` nguyên (không tự xoá khi đổi lọc): đổi lọc rồi đổi lại thì lựa chọn còn đó.
   *   Cái được sửa là PHÉP ĐẾM và PHÉP GỬI, không phải trí nhớ.
   */
  const dsTick = useMemo(() => dsCho.filter((t) => tick.has(t.id)), [dsCho, tick]);
  const soTickBiGiau = tick.size - dsTick.length;

  const moTaLoc = [
    locThieu === 'tat-ca' ? 'tat-ca' : locThieu,
    timTu.trim() ? `tim-${timTu.trim()}` : '',
  ].filter(Boolean).join('_');

  const xuatCsv = () => {
    const csv = ghepCsv(
      ['hardwareId', 'deviceId', 'Tên tủ', 'Mã tủ', 'Tuyến chiếu sáng', 'Lần đầu thấy', 'Lần cuối thấy', 'Thiếu'],
      dsCho.map((t) => [
        // ⚠ hardwareId + deviceId đi qua `oVanBan()`: Excel sẽ ăn mất số 0 đầu hoặc đổi MAC
        // sang số khoa học, mà đó đúng là cột danh tính DUY NHẤT không tái tạo được.
        oVanBan(t.hardwareId),
        oVanBan(t.deviceId ?? ''),
        oAnToan(t.name), oAnToan(t.code ?? ''),
        oAnToan(t.tenDuong ?? '(chưa gán)'),
        oAnToan(fmtDateTime(t.createdAt)),
        oAnToan(t.lastSeenAt ? fmtDateTime(t.lastSeenAt) : 'chưa lên mạng lần nào'),
        oAnToan(NHAN_THIEU[t.thieu]),
      ]),
    );
    taiVe(tenTepCsv('danh-sach-cho', moTaLoc), csv);
  };
  const lamMoi = () => {
    qc.invalidateQueries({ queryKey: qk.routes });
    qc.invalidateQueries({ queryKey: qk.tree });
    qc.invalidateQueries({ queryKey: qk.danhSachCho });
    qc.invalidateQueries({ queryKey: qk.overview });
    qc.invalidateQueries({ queryKey: qk.choDuyet });
    qc.invalidateQueries({ queryKey: qk.biChan });
  };

  return (
    <div className="space-y-4">
      {/* ══════════ ① TIÊU ĐỀ TRANG ══════════ */}
      {/*
        ⚠ Tiêu đề trang khớp CHÍNH XÁC nhãn thanh bên (TK-33b: "Tủ điều khiển"), còn
        thẻ ngay dưới mang tên ngắn. Để cả hai cùng là "Tuyến chiếu sáng" thì trang có hai
        tiêu đề y hệt nhau chồng lên nhau — mắt đọc thành một lỗi lặp, không thành cấu trúc.
      */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0">
          <h1 className="chu-tieude m-0 text-[26px] font-semibold leading-tight text-chu-1">
            Quản lý tủ
          </h1>
          {/* TK-24: bỏ dòng phụ dưới tiêu đề trang — L-KHÔNG-PHỒNG (tệp 29 điều 10): "không dòng phụ diễn giải trong app". */}
        </div>
        <Button className="ml-auto" variant="primary" icon={<Plus className="h-4 w-4" />}
                onClick={() => setDangSua('moi')}>
          Thêm tuyến chiếu sáng
        </Button>
      </div>

      {/* ══════════ ② HAI Ô SỐ — TK-38 việc 3 (lee 23/10): Tổng tuyến · Tủ: tìm thấy · đã gán · chưa gán ══════════ */}
      <div data-vung="so-tuyen" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <OSoLon
          icon={<MapPin className="h-5 w-5" />} mau="nhan" nhan="Tổng tuyến" tri={soLieu.tongTuyen}
        />
        <OSoLon
          icon={<RouteIcon className="h-5 w-5" />} mau="tot" nhan="Tủ: tìm thấy · đã gán · chưa gán"
          tri={demTu.data ? `${demTu.data.timThay} · ${demTu.data.daGan} · ${demTu.data.chuaGan ?? '—'}` : null}
        />
      </div>

      {/* TK-38 việc 3: ô "Tuyến trùng tên" bỏ — chỉ còn một dòng nhỏ trên đầu bảng khi có trùng (> 0) */}
      {soLieu.soTrung !== null && soLieu.soTrung > 0 && (
        <p className="m-0 text-sm text-canh" data-o="dong-trung-ten"><AlertTriangle className="mr-1 inline h-4 w-4 align-[-3px]" />{soLieu.soTrung} tuyến trùng tên</p>
      )}

      {/* ══════════ ③ BẢNG TUYẾN ══════════ */}
      <Card
        title="Danh sách tuyến chiếu sáng"
        subtitle={soLieu.tongTuyen === null ? 'đang đọc…' : `${soLieu.tongTuyen} tuyến`}
        /*
         * TK-24 — L-KHÔNG-DIỄN-GIẢI (tệp 29 điều 6): hai câu giải thích rời khỏi phụ đề và băng xanh, vào ⓘ.
         * ⚠ Câu YC-21 ("địa chỉ mới là thứ phân biệt hai tuyến cùng tên") GIỮ NGUYÊN TỪNG CHỮ như lee chốt —
         *   chỉ đổi CHỖ (băng luôn hiện → ⓘ một bấm). Lee bác được: trả băng lại là một dòng.
         */
        goiY={{ nhan: 'Danh sách tuyến', noiDung: 'Bấm "Chi tiết" để xem tủ của tuyến ngay tại chỗ. Tên tuyến thường là tên đường — địa chỉ mới là thứ phân biệt hai tuyến cùng tên.' }}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={kw} onChange={(e) => setKw(e.target.value)}
              placeholder="Tìm theo tên, mã hoặc địa chỉ…"
              className="inp w-56"
            />
            <select className="inp w-44" value={locNoi}
                    onChange={(e) => setLocNoi(e.target.value as MucNoi | 'tat-ca')}>
              <option value="tat-ca">Tất cả trạng thái</option>
              <option value="du">Nối đủ</option>
              <option value="mot-phan">Nối một phần</option>
              <option value="mat-het">Mất hết kết nối</option>
              <option value="chua-co-tu">Chưa có tủ</option>
              <option value="chua-do-duoc">Chưa đo được</option>
            </select>
          </div>
        }
      >
        {/* Băng YC-21 cũ nằm ở đây — TK-24 chuyển câu ấy (nguyên văn) vào ⓘ cạnh tiêu đề thẻ. */}
        {routes.isLoading ? <Spinner />
          : routes.error ? <ErrorBox error={routes.error} onRetry={() => routes.refetch()} />
          : dsHien.length === 0 ? (
            <EmptyState
              icon={<RouteIcon className="h-8 w-8" />}
              title={(routes.data ?? []).length === 0 ? 'Chưa có tuyến chiếu sáng nào' : 'Không có tuyến nào khớp'}
              hint={(routes.data ?? []).length === 0
                ? 'Thêm tuyến chiếu sáng trước, rồi gán tủ vào.'
                : 'Ô tìm khớp cả tên lẫn địa chỉ; bộ lọc trạng thái chỉ giấu bớt.'}
            />
          ) : (
            <div className="tbl-wrap">
              {/*
                * ═══════════════════════════════════════════════════════════════════════════
                * 🔴 `GD-09` mục 6, VÒNG BA — `table-fixed` + `<colgroup>`, ĐÚNG NHƯ MẪU `_BUS/26`
                * ĐÃ GHI TỪ ĐẦU, và tôi đã bỏ qua ở `GD-06`.
                *
                * Hai vòng trước tôi chữa bằng cách CHẶN TRẦN từng ô (`max-w-[22rem]`, rồi
                * `max-w-[30rem]`) — tức là tự cộng bề rộng bảy cột trong đầu rồi hy vọng tổng
                * nhỏ hơn khung. Ảnh 1440 px bác cả hai lần: bảng vẫn tràn, bốn cột phải vẫn
                * khuất, chỉ khuất ít hơn.
                * ⛔ ĐÓ LÀ MỘT PHÉP TÍNH NHẨM ĐỘI LỐT MỘT THIẾT KẾ. `table-layout: fixed` đảo
                *   chiều quyết định: **bề rộng do CỘT định trước, nội dung phải tự cắt** — cùng
                *   cơ chế đã dựng cho bảng tuyến trong hộp tài khoản (`GD-02b`), và cùng lý do.
                *   Với `auto` thì ô không bao giờ hẹp hơn nội dung, nên `truncate` chỉ chạy khi
                *   tôi đoán đúng trần — và tôi đoán sai hai lần liên tiếp.
                * ⚠ `minmax`-kiểu-lưới không dùng được ở `<table>`; `<colgroup>` là cách nói cùng
                *   một điều bằng ngôn ngữ của bảng.
                * ═══════════════════════════════════════════════════════════════════════════
                */}
              {/*
                * 🔴 09/10/2026 — SÀN RIÊNG `min-w-[880px]`, KHÔNG dùng `min-w-[560px]` của `.tbl`.
                *
                * Đo trên máy thật ở 375 px: cột TÊN TUYẾN rộng **0 px**, nằm cùng `x = 65` với cột
                * ĐỊA CHỈ ⇒ hai tiêu đề vẽ chồng lên nhau (ảnh lee gửi đọc ra "DỊN CSỐỈ ẾN").
                * Cộng ra được: `<colgroup>` cố định `36+80+104+120+176 = 516 px` + Địa chỉ `18 %`;
                * ở sàn 560 chỉ còn `560 − 516 = 44 px` chia cho HAI cột co giãn ⇒ Địa chỉ 44, Tên 0.
                * `.tbl th{whitespace-nowrap}` nên chữ không xuống dòng mà tràn đè sang ô bên.
                *
                * Để Tên tuyến ≥ 200 px: `516 + 0,18W + 200 ≤ W` ⇒ **W ≥ 873** ⇒ lấy tròn 880.
                * ⚠ Sàn này phải lớn hơn `.tbl` mới có tác dụng: `.tbl` ở `@layer components`,
                *   `min-w-[880px]` ở `@layer utilities` — utilities xếp sau nên thắng. Đổi `.tbl`
                *   thành một lớp thường là làm dòng này im lặng mất tác dụng.
                */}
              <table className="tbl table-fixed min-w-[900px]">
                {/*
                  * ⚠ BỀ RỘNG ĐO ĐƯỢC, KHÔNG ĐOÁN. Khung chứa bảng rộng **926 px** ở cửa sổ
                  *   1440 (đo bằng `getBoundingClientRect` trong bộ tự chụp, không nhẩm). Với
                  *   `table-fixed`, cột TÊN lấy phần còn lại, nên năm cột cố định + `%` của cột
                  *   địa chỉ quyết định nó còn bao nhiêu:
                  *       36 + 80 + 104 + 120 + 176 = 516 px cố định
                  *       địa chỉ 18 %  ≈ 167 px
                  *       ⇒ TÊN ≈ 243 px — rộng hơn địa chỉ, đúng thứ tự quan trọng.
                  * 🔴 Bản trước để địa chỉ 26 % và cột TÊN chỉ còn **107 px** — cột quan trọng
                  *   nhất lại bị cắt nhiều nhất. Con số ấy tôi cũng không nhẩm ra được; ảnh và
                  *   phép đo mới nói ra.
                  */}
                <colgroup>
                  <col className="w-9" />
                  <col />
                  <col className="w-[18%]" />
                  <col className="w-[80px]" />
                  <col className="w-[104px]" />
                  <col className="w-[140px]" />
                  <col className="w-[176px]" />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <ThXep cot="ten" dang={xepTheo} nguoc={xepNguoc} onBam={doiXep}>Tên tuyến</ThXep>
                    <ThXep cot="diaChi" dang={xepTheo} nguoc={xepNguoc} onBam={doiXep}>Địa chỉ</ThXep>
                    <ThXep cot="soTu" dang={xepTheo} nguoc={xepNguoc} onBam={doiXep}>Số tủ</ThXep>
                    <th>Mã tuyến</th>
                    <ThXep cot="noi" dang={xepTheo} nguoc={xepNguoc} onBam={doiXep}>Trạng thái</ThXep>
                    <th className="text-right">Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {dsHien.map((r, i) => {
                    const soTu = r._count?.cabinets ?? 0;
                    const mo = moRong === r.id;
                    const dc = diaChi(r);
                    const noi = docNoi(r, cay.data);
                    const tuCuaDuong = (cay.data ?? []).find((x) => x.id === r.id)?.cabinets ?? [];
                    return (
                      <Fragment key={r.id}>
                        <tr className={mo ? 'bg-nen-2' : undefined}>
                          <td className="text-chu-3">{i + 1}</td>
                          {/*
                            * 🔴 `tenDayDu` — KHÔNG phải `r.name`. Cột Địa chỉ ngay bên phải là
                            *   vế thứ hai của luật YC-21, nhưng cột này vẫn mang danh tính ĐẦY
                            *   ĐỦ do máy chủ dựng: nếu một ngày ai đó ẩn cột Địa chỉ đi thì
                            *   trang vẫn không vi phạm.
                            */}
                          {/*
                            * 🔴 `GD-09` mục 6, VÒNG HAI — ẢNH TỰ CHỤP BẮT ĐƯỢC CÁI TÔI SỬA HỤT.
                            *
                            * Vòng một tôi chỉ chặn cột ĐỊA CHỈ (`max-w-[22rem]`) và tưởng xong.
                            * Ảnh 1440 px cho thấy bảng vẫn tràn ngang: cột TÊN không có trần
                            * nào, nên một `tenDayDu` dài đẩy hết bốn cột bên phải ra ngoài mép —
                            * đúng triệu chứng lee báo ("chỉ còn thấy Sửa"), chỉ đổi nguyên nhân.
                            *
                            * ⛔ `truncate` MỘT MÌNH KHÔNG ĐỦ: nó cần một cái trần để cắt vào.
                            *   Trong một `<td>` co giãn tự do thì ô cứ rộng ra theo nội dung và
                            *   `text-overflow` không bao giờ chạy — **đúng bài học `GD-02d`**,
                            *   tái diễn ở một cái bảng khác vì tôi chỉ chép cái `truncate` mà
                            *   không chép cái trần đi kèm.
                            * ⚠ Và ba phép đo đều XANH suốt: `tsc`, mục `[111]`, bộ phá. Cả ba
                            *   đọc cái tôi VIẾT; chỉ tấm ảnh đọc cái trình duyệt LÀM.
                            */}
                          <td>
                            <span className="flex min-w-0 items-center gap-2">
                              <MapPin className="h-4 w-4 shrink-0 text-nhan" />
                              <span className="truncate font-medium text-chu-1" title={r.tenDayDu}>
                                {r.tenDayDu}
                              </span>
                            </span>
                          </td>
                          {/*
                            * ⛔ KHÔNG ĐỊA CHỈ ⇒ NÓI RA BẰNG CHỮ HỔ PHÁCH. Đây CHÍNH LÀ ca mà tên
                            *   đường đứng một mình — thứ YC-21 cấm — nên nó phải kêu lên, không
                            *   được để trống. Hổ phách vì đúng nghĩa `[95]`: cần người xử lý.
                            */}
                          {/*
                            * ➕ `GD-09` mục 6 — CỘT ĐỊA CHỈ CẮT "…" + `title`, không rớt ba dòng.
                            *
                            * 🔴 Ở 1440 px lee chụp được ba dòng: `.tbl` không đặt bề rộng cột
                            *   nào, nên một địa chỉ dài đẩy cột tên hẹp lại rồi tự xuống dòng —
                            *   và mỗi hàng cao gấp ba, bảng đọc thành một khối chữ.
                            * ⛔ CẮT THÌ PHẢI CÓ ĐƯỜNG ĐỌC LẠI (`title`) — cùng luật đã dựng cho
                            *   ô tên tuyến ở `GD-02d`. Địa chỉ là vế PHÂN BIỆT hai tuyến cùng
                            *   tên (YC-21), nên một địa chỉ cụt mà không tra lại được là đúng
                            *   chỗ người ta chọn nhầm tuyến.
                            * ⚠ Ô "chưa có địa chỉ" thì KHÔNG cắt: nó là một CÂU cảnh báo, không
                            *   phải một giá trị — cắt một lời cảnh báo là bỏ mất vế quan trọng.
                            */}
                          <td className="text-chu-2">
                            {dc
                              ? <span className="block truncate" title={dc}>{dc}</span>
                              : <span className="text-canh">chưa có địa chỉ — hai tuyến cùng tên sẽ không phân biệt được</span>}
                          </td>
                          <td><Badge tone="blue">{soTu} tủ</Badge></td>
                          <td className="text-chu-2">
                            {r.code ? <span className="so-do">{r.code}</span> : <span className="text-chu-3">—</span>}
                          </td>
                          <td>
                            {/*
                              * ⚠ CHẤM Ở ĐÂY KHÔNG DÙNG `StatusDot` — `StatusDot` chỉ biết hai
                              *   màu (lá / xám) theo `online`, mà huy hiệu này có BỐN mức. Một
                              *   chấm xám nằm trong một huy hiệu cam là hai câu trái nhau trên
                              *   cùng 20 px. Chấm ở đây mượn màu của chính huy hiệu.
                              * ⛔ Và KHÔNG có chấm ở hai mức "chưa": một cái chấm trông như một
                              *   phép đo, mà hai mức ấy nghĩa là CHƯA đo được.
                              */}
                            <Badge tone={MAU_NOI[noi.muc]}>
                              {noi.muc !== 'chua-do-duoc' && noi.muc !== 'chua-co-tu' && (
                                <span aria-hidden="true">●</span>
                              )}
                              {chuNoi(noi)}
                            </Badge>
                          </td>
                          {/*
                            * ➕ `GD-09` mục 6 — CỘT HÀNH ĐỘNG **KHÔNG XUỐNG DÒNG**. lee chụp ở
                            *   1440 px chỉ còn thấy "Sửa"; "Chi tiết" và nút Xoá bị đẩy khuất.
                            * 🔴 Ba nút bị đẩy đi mà KHÔNG có gì báo — không dấu "…", không thanh
                            *   cuộn ở đúng chỗ ấy. Một nút vô hình khác hẳn một nút bị khoá:
                            *   cái thứ hai nói ra rằng nó tồn tại.
                            * ⛔ `whitespace-nowrap` + `w-px` (co về đúng nội dung) là cặp giữ cả
                            *   ba nút trên một hàng và KHÔNG ăn thêm chỗ của cột địa chỉ.
                            */}
                          <td className="w-px whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />}
                                      onClick={() => setDangSua(r)}>Sửa</Button>
                              {/*
                                * ⚠ "Chi tiết" MỜ khi tuyến chưa có tủ — mở ra một danh sách rỗng
                                *   không trả lời được câu nào, và một nút mở ra khoảng trắng dạy
                                *   người dùng thôi bấm nút ấy.
                                */}
                              <Button size="sm" variant="ghost" disabled={soTu === 0}
                                      icon={mo ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      onClick={() => setMoRong(mo ? null : r.id)}>Chi tiết</Button>
                              <Button size="sm" variant="ghost" className="text-nguy"
                                      icon={<Trash2 className="h-4 w-4" />}
                                      onClick={() => setDangXoa(r)} aria-label={`Xoá ${r.tenDayDu}`} />
                            </div>
                          </td>
                        </tr>
                        {mo && (
                          <tr>
                            {/*
                              * Danh sách tủ mở ngay tại chỗ, KHÔNG sang trang khác. "Gỡ tủ" là
                              * nghịch đảo của "Gán tuyến" ở khối bên dưới — hai nút phải nhìn
                              * thấy nhau thì người dùng mới hiểu cái này đẩy tủ sang đâu.
                              * ⚠ `GD-06` — hàng mở nay TRẢI HẾT BỀ NGANG (`colSpan`), nên KHÔNG
                              *   còn hai khung cuộn lồng nhau như bản cũ. Cái khó dùng ấy mất đi
                              *   là hệ quả của bảng, không phải một việc riêng.
                              */}
                            <td colSpan={7} className="bg-nen-2/60">
                              {tuCuaDuong.length === 0 ? (
                                <p className="py-2 text-xs text-chu-2">
                                  {cay.isLoading ? 'Đang tải…' : 'Không đọc được danh sách tủ của tuyến chiếu sáng này.'}
                                </p>
                              ) : (
                                <ul className="divide-y divide-vien">
                                  {tuCuaDuong.map((t) => (
                                    <li key={t.id} className="flex items-center gap-2 py-1.5">
                                      <StatusDot online={t.status === 'ONLINE'} />
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-xs font-medium text-chu-1">{t.code} · {t.name}</p>
                                        <p className="truncate text-xs text-chu-3">{t.hardwareId}</p>
                                      </div>
                                      <NutTuThuong tuId={t.id} ten={t.name} />
                                      <Button size="sm" variant="ghost" icon={<Unplug className="h-3.5 w-3.5" />}
                                              onClick={() => setDangGo({ tu: t, duong: r })}>
                                        Gỡ khỏi tuyến
                                      </Button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      {/* ══════════ ③b TỦ CHỜ DUYỆT (TK-17) ══════════ */}
      {laSuper && <KhoiChoDuyet onDoi={lamMoi} />}

      {/* ══════════ ④ DANH SÁCH CHỜ GÁN TUYẾN ══════════ */}
      {laSuper && <Card
        title="Danh sách chờ gán tuyến"
        goiY={{ nhan: 'Danh sách chờ gán tuyến', noiDung: 'Tủ còn thiếu tên, thiếu tuyến chiếu sáng, hoặc cả hai. Chúng vẫn chạy ngoài hiện trường — chỉ là chưa xong hồ sơ.' }}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input value={timTu} onChange={(e) => setTimTu(e.target.value)}
                   placeholder="Tìm theo tên tủ, mã, MAC…" className="inp w-52" />
            {cho.data?.length ? <Badge tone="amber">{dsCho.length}/{cho.data.length} tủ</Badge> : null}
            <Button size="sm" variant="secondary" icon={<Download className="h-4 w-4" />}
                    disabled={dsCho.length === 0} onClick={xuatCsv}>
              Xuất CSV
            </Button>
          </div>
        }
      >
        {/*
          Bộ lọc nằm NGAY TRÊN danh sách, và nút Xuất CSV xuất ĐÚNG cái đang hiện — hai thứ
          phải nhìn thấy nhau, không thì người bấm không biết mình vừa xuất bao nhiêu.
        */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {([['tat-ca', 'Tất cả'], ['chua-dat-ten', NHAN_THIEU['chua-dat-ten']],
             ['chua-gan-duong', NHAN_THIEU['chua-gan-duong']], ['ca-hai', 'Cả hai']] as const).map(([ma, nhan]) => (
            <button key={ma} onClick={() => setLocThieu(ma as Thieu | 'tat-ca')}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                locThieu === ma ? 'border-nhan/50 bg-nhan/15 text-nhan' : 'border-vien text-chu-2 hover:bg-nen-2'}`}>
              {nhan}
              <span className="ml-1.5 text-chu-3">
                {ma === 'tat-ca' ? (cho.data ?? []).length : (cho.data ?? []).filter((t) => t.thieu === ma).length}
              </span>
            </button>
          ))}
        </div>

        {cho.isLoading ? <Spinner />
          : cho.error ? <ErrorBox error={cho.error} onRetry={() => cho.refetch()} />
          : dsCho.length === 0 ? (
            <EmptyState
              icon={<Unplug className="h-8 w-8" />}
              title={(cho.data ?? []).length === 0 ? 'Không có tủ nào đang chờ' : 'Không tủ nào khớp bộ lọc'}
              hint={(cho.data ?? []).length === 0
                ? 'Tủ mới cắm lần đầu, hoặc tủ vừa bị gỡ khỏi một tuyến chiếu sáng, sẽ xuất hiện ở đây.'
                : 'Đổi bộ lọc hoặc xoá từ khoá tìm.'}
            />
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th className="w-9">
                      {/*
                        * ⛔ TICK-TẤT-CẢ CHỈ TICK NHỮNG TỦ ĐANG HIỆN, và bỏ tick cũng chỉ bỏ
                        *   những tủ đang hiện. Một cái ô "tất cả" động tới thứ ngoài màn hình
                        *   là cách nhanh nhất để gán nhầm cả một lô.
                        */}
                      <input
                        type="checkbox" className="h-4 w-4 accent-[rgb(var(--nhan))]"
                        aria-label="Chọn mọi tủ đang hiện"
                        checked={dsCho.length > 0 && dsTick.length === dsCho.length}
                        ref={(el) => {
                          if (el) el.indeterminate = dsTick.length > 0 && dsTick.length < dsCho.length;
                        }}
                        onChange={(e) => setTick((cu) => {
                          const moi = new Set(cu);
                          for (const t of dsCho) { if (e.target.checked) moi.add(t.id); else moi.delete(t.id); }
                          return moi;
                        })}
                      />
                    </th>
                    <th>Tủ</th>
                    <th>Mã tủ / gateway</th>
                    <th>Lần thấy cuối</th>
                    <th>Kết nối</th>
                    <th>Hồ sơ</th>
                    <th className="text-right">Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {dsCho.map((t) => (
                    <HangCho
                      key={t.id} tu={t} tick={tick.has(t.id)}
                      onTick={(v) => setTick((cu) => {
                        const moi = new Set(cu);
                        if (v) moi.add(t.id); else moi.delete(t.id);
                        return moi;
                      })}
                      onDatTen={() => setDangDatTen(t)}
                      onGanDuong={() => setDangGan(t)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

        {/*
          * ═════════════════════════════════════════════════════════════════════════════════
          * THANH HÀNH ĐỘNG HÀNG LOẠT — hiện khi có ≥1 tủ ĐANG HIỆN được tick.
          *
          * 🔴 VÀ NÓ NÓI RA CÁCH NÓ LÀM VIỆC: *"gửi từng tủ một"*. Không có API hàng loạt; mỗi
          *   tủ là một lời gọi `PATCH`. Người bấm phải biết điều đó TRƯỚC khi bấm, vì nó quyết
          *   định chuyện gì xảy ra khi đứt giữa chừng: một phần đã xong, một phần chưa — và
          *   hộp kết quả sẽ nói rõ phần nào.
          * ⛔ VÀ NÓI RA SỐ TỦ ĐÃ TICK NHƯNG ĐANG BỊ LỌC GIẤU. Không nói thì người dùng tick 8
          *   tủ, gõ một chữ vào ô tìm, thấy "Đã chọn 2 tủ" và tưởng mình vừa mất 6 lựa chọn.
          * ═════════════════════════════════════════════════════════════════════════════════
          */}
        {dsTick.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-nhan/40 bg-nhan/10 px-3 py-2">
            <b className="text-sm text-chu-1">Đã chọn {dsTick.length} tủ</b>
            {soTickBiGiau > 0 && (
              <span className="text-xs text-canh">
                (+{soTickBiGiau} tủ đã tick đang bị bộ lọc giấu — KHÔNG nằm trong lần gửi này)
              </span>
            )}
            <Button size="sm" variant="secondary" onClick={() => setHangLoat('gan')}>
              Gán vào tuyến…
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setHangLoat('dat-ten')}>
              Đặt tên hàng loạt
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setTick(new Set())}>Bỏ chọn</Button>
            <span className="ml-auto text-xs text-chu-3">
              Gán hàng loạt = gửi từng tủ một, có báo từng dòng thành công/lỗi
            </span>
          </div>
        )}
      </Card>}

      {dangSua && (
        <FormConDuong
          duong={dangSua === 'moi' ? null : dangSua}
          onDong={() => setDangSua(null)}
          onXong={(msg) => { toast.success(msg); setDangSua(null); lamMoi(); }}
        />
      )}
      {dangXoa && (
        <HopThoaiXoa
          duong={dangXoa}
          onDong={() => setDangXoa(null)}
          onXong={(msg) => { toast.success(msg); setDangXoa(null); lamMoi(); }}
        />
      )}
      {dangGo && (
        <HopThoaiGoTu
          tu={dangGo.tu} duong={dangGo.duong}
          onDong={() => setDangGo(null)}
          onXong={(msg) => { toast.success(msg); setDangGo(null); lamMoi(); }}
        />
      )}
      {dangGan && (
        <HopThoaiGan
          tu={dangGan} duongs={routes.data ?? []}
          onDong={() => setDangGan(null)}
          onXong={(msg) => { toast.success(msg); setDangGan(null); lamMoi(); }}
        />
      )}
      {dangDatTen && (
        <HopThoaiDatTen
          tu={dangDatTen}
          onDong={() => setDangDatTen(null)}
          onXong={(msg) => { toast.success(msg); setDangDatTen(null); lamMoi(); }}
        />
      )}
      {hangLoat && (
        <HopThoaiHangLoat
          viec={hangLoat} tus={dsTick} duongs={routes.data ?? []}
          onDong={() => setHangLoat(null)}
          onXong={(conLoi, msg) => {
            toast.success(msg);
            /*
             * ⛔ DÒNG LỖI GIỮ TICK (phiếu chốt) — và dòng XONG thì mất tick. Bỏ sạch tick là
             *   bắt người dùng đi tìm lại đúng những dòng vừa hỏng giữa một danh sách dài;
             *   giữ sạch tick là để họ gửi lại cả những dòng đã xong.
             */
            setTick(new Set(conLoi));
            lamMoi();
          }}
        />
      )}
    </div>
  );
}

/**
 * Ô tiêu đề BẤM ĐƯỢC để sắp xếp. Mũi tên chỉ hiện ở cột đang xếp — một bảng mà cột nào cũng
 * có mũi tên là một bảng không nói được cột nào đang quyết định thứ tự.
 */
/**
 * TK-41 §4 (lee 24/10): "Đánh dấu tủ thường" CHUYỂN từ trang Cài đặt ban đầu về đây (chi tiết tủ), chỉ ADMIN / SUPER_ADMIN, chỉ
 * khi máy chủ bật NEMA. Tủ đã đánh dấu ⇒ chip "Tủ thường" + "Bỏ đánh dấu" (SUPER_ADMIN, như TK-40). Không còn tự kết luận theo giờ.
 */
function NutTuThuong({ tuId, ten }: { tuId: string; ten: string }) {
  const { hasRole } = useAuth();
  const coNema = useNemaBat().trangThai === 'bat';
  const quyen = hasRole('ADMIN');
  const cay = useCayCaiDat(quyen && coNema);
  const qc = useQueryClient(); const toast = useToast();
  const [hoi, setHoi] = useState(false);
  const lam = useMutation({
    mutationFn: (viec: 'tu-thuong' | 'thu-lai') => api.post(`/api/nema/cai-dat/tu/${tuId}/${viec}`, {}),
    onSuccess: () => { setHoi(false); void qc.invalidateQueries({ queryKey: ['cai-dat-ban-dau'] }); },
    onError: (e) => toast.error('Không làm được', errorMessage(e)),
  });
  if (!quyen || !coNema) return null;
  const t = (cay.data ?? []).flatMap((r) => r.tu).find((x) => x.id === tuId);
  if (!t) return null;
  if (t.tuThuong) return (
    <span className="flex items-center gap-1.5" data-tu-thuong={tuId}>
      <Badge tone="slate">Tủ thường</Badge>
      {hasRole('SUPER_ADMIN') && <Button size="sm" variant="ghost" loading={lam.isPending} onClick={() => lam.mutate('thu-lai')}>Bỏ đánh dấu</Button>}
    </span>
  );
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setHoi(true)} data-o="nut-tu-thuong">Đánh dấu tủ thường</Button>
      <ConfirmDialog open={hoi} title="Đánh dấu tủ thường" message={`Tủ ${ten} không có mạng liên kết đèn — đánh dấu là tủ thường?`}
        confirmText="Đánh dấu" danger={false} loading={lam.isPending} onConfirm={() => lam.mutate('tu-thuong')} onCancel={() => setHoi(false)} />
    </>
  );
}

function ThXep({ cot, dang, nguoc, onBam, children }: {
  cot: CotXep; dang: CotXep | null; nguoc: boolean; onBam: (c: CotXep) => void; children: ReactNode;
}) {
  const dangXep = dang === cot;
  return (
    <th>
      <button type="button" onClick={() => onBam(cot)}
              className={`inline-flex items-center gap-1 uppercase tracking-wide transition hover:text-chu-1 ${dangXep ? 'text-chu-1' : ''}`}
              aria-sort={dangXep ? (nguoc ? 'descending' : 'ascending') : 'none'}>
        {children}
        <ArrowUpDown className={`h-3 w-3 ${dangXep ? 'text-nhan' : 'text-chu-3'}`} />
        {dangXep && <span className="text-xs text-nhan">{nguoc ? '↓' : '↑'}</span>}
      </button>
    </th>
  );
}

/* ------------------------------------------------------------------ thêm · sửa */

function FormConDuong({ duong, onDong, onXong }: {
  duong: RouteRow | null; onDong: () => void; onXong: (msg: string) => void;
}) {
  const [f, setF] = useState({
    name: duong?.name ?? '', code: duong?.code ?? '',
    phuong: duong?.phuong ?? '',
    tinh: duong?.tinh ?? '', note: duong?.note ?? '',
  });
  const [loi, setLoi] = useState<string | null>(null);
  const dat = (k: keyof typeof f) => (e: any) => setF({ ...f, [k]: e.target.value });

  const luu = useMutation({
    mutationFn: () => {
      const than = {
        name: f.name.trim(),
        code: f.code.trim() || undefined,
        phuong: f.phuong.trim() || undefined,
        tinh: f.tinh.trim() || undefined,
        note: f.note.trim() || undefined,
      };
      return duong ? api.patch(`/api/routes/${duong.id}`, than) : api.post('/api/routes', than);
    },
    onSuccess: () => onXong(duong ? 'Đã lưu tuyến chiếu sáng' : 'Đã thêm tuyến chiếu sáng'),
    onError: (e) => setLoi(errorMessage(e)),
  });

  /* TK-21: Xã/Phường BẮT BUỘC (tạo mới VÀ sửa) — máy chủ cũng chặn 400; nút Lưu khoá sẵn ở đây. */
  const thieuPhuong = !f.phuong.trim();

  return (
    <Modal open title={duong ? 'Sửa tuyến chiếu sáng' : 'Thêm tuyến chiếu sáng'} onClose={onDong}
      footer={
        <>
          <Button variant="ghost" onClick={onDong}>Huỷ</Button>
          <Button variant="primary" loading={luu.isPending}
                  disabled={!f.name.trim() || thieuPhuong} onClick={() => { setLoi(null); luu.mutate(); }}>
            {duong ? 'Lưu' : 'Thêm'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Tên đường" required hint="Chỉ để người đọc — KHÔNG phải danh tính. Trùng tên là bình thường.">
          <input className="inp" value={f.name} onChange={dat('name')} placeholder="Nguyễn Huệ" />
        </Field>

        {/* TK-21 (lee 06/10): Tỉnh/Thành phố → Xã/Phường → Tuyến. KHÔNG còn ô quận/huyện. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Xã/Phường" required><input className="inp" value={f.phuong} onChange={dat('phuong')} placeholder="Phường Bến Thành" /></Field>
          <Field label="Tỉnh/Thành phố"><input className="inp" value={f.tinh} onChange={dat('tinh')} placeholder="TP. Hồ Chí Minh" /></Field>
        </div>

        {/*
          TK-21: Xã/Phường là cấp DUY NHẤT dưới tỉnh và là một phần khoá trùng (tên, phường, tỉnh) — nên
          BẮT BUỘC, cho cả tuyến cũ đang được sửa. Nói rõ vì sao nút Lưu đang khoá.
        */}
        {thieuPhuong && (
          <p className="rounded-lg bg-canh-nen px-3 py-2 text-xs text-canh">
            ⚠ Chưa nhập Xã/Phường — bắt buộc{duong ? ', kể cả khi sửa một tuyến cũ' : ''}. Hai tuyến cùng tên trong
            cùng tỉnh được phân biệt bằng Xã/Phường.
          </p>
        )}

        <Field label="Mã gợi nhớ" hint="Tuỳ chọn. Sau YC-21 mã KHÔNG còn là danh tính — bỏ trống cũng được.">
          <input className="inp" value={f.code} onChange={dat('code')} placeholder="NVL" />
        </Field>
        <Field label="Ghi chú"><input className="inp" value={f.note} onChange={dat('note')} /></Field>

        {loi && <p className="whitespace-pre-line rounded-lg bg-nguy-nen px-3 py-2 text-xs text-nguy">{loi}</p>}
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------------- xoá */

/**
 * 🔴 KHÔNG dùng `ConfirmDialog` chung ở đây, và đó là chủ ý.
 *
 * Máy chủ đòi `?xacNhanSoTu=<n>` đúng bằng số tủ sẽ bị gỡ ra — để một cú bấm nhầm không
 * gỡ được 14 cái tủ khỏi mọi tài khoản trong im lặng. Một hộp thoại chỉ có nút "Xác nhận"
 * sẽ **tự điền hộ con số ấy**, tức là gỡ đúng cái chốt vừa dựng. Nên người dùng phải TỰ GÕ
 * con số vào. Đó là cách duy nhất một xác nhận thật sự xác nhận điều gì.
 */
function HopThoaiXoa({ duong, onDong, onXong }: {
  duong: RouteRow; onDong: () => void; onXong: (msg: string) => void;
}) {
  const soTu = duong._count?.cabinets ?? 0;
  const [go, setGo] = useState('');
  const [loi, setLoi] = useState<string | null>(null);
  const dungSo = soTu === 0 || go.trim() === String(soTu);

  const xoa = useMutation({
    mutationFn: () => api.del(`/api/routes/${duong.id}${soTu > 0 ? `?xacNhanSoTu=${soTu}` : ''}`),
    onSuccess: () => onXong(soTu > 0
      ? `Đã xoá tuyến chiếu sáng. ${soTu} tủ chuyển sang "chưa gán tuyến".`
      : 'Đã xoá tuyến chiếu sáng.'),
    onError: (e) => setLoi(errorMessage(e)),
  });

  return (
    <Modal open title="Xoá tuyến chiếu sáng" onClose={onDong}
      footer={
        <>
          <Button variant="ghost" onClick={onDong}>Huỷ</Button>
          <Button variant="danger" loading={xoa.isPending} disabled={!dungSo}
                  onClick={() => { setLoi(null); xoa.mutate(); }}>
            Xoá tuyến chiếu sáng
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {/* 🔴 tenDayDu — "Xoá đường Nguyễn Huệ?" là câu hỏi không trả lời được */}
        <p>Xoá <b>{duong.tenDayDu}</b>?</p>

        {soTu > 0 ? (
          <>
            <div className="rounded-lg bg-canh-nen px-3 py-2 text-xs text-canh">
              <p><b>{soTu} tủ</b> đang thuộc tuyến chiếu sáng này sẽ chuyển về trạng thái <b>chưa gán tuyến</b>.</p>
              <ul className="mt-1 list-disc pl-4">
                <li>Tủ <b>KHÔNG</b> bị xoá — hàng, lịch sử, số đo, cảnh báo giữ nguyên.</li>
                <li><b>KHÔNG</b> có gói tin nào gửi xuống tủ. Đèn ngoài hiện trường vẫn chạy theo lịch cũ.</li>
                <li>Nhưng chúng sẽ rời khỏi mọi tài khoản cho tới khi được gán tuyến lại.</li>
                <li>Tìm lại chúng ở khối <b>"Danh sách chờ gán tuyến"</b> ngay trang này.</li>
              </ul>
            </div>
            <Field label={`Gõ số ${soTu} để xác nhận`} hint="Máy chủ đòi đúng con số này — không tự điền hộ được.">
              <input className="inp" value={go} onChange={(e) => setGo(e.target.value)}
                     inputMode="numeric" placeholder={String(soTu)} />
            </Field>
          </>
        ) : (
          <p className="text-xs text-chu-2">Tuyến chiếu sáng này chưa có tủ nào.</p>
        )}

        {loi && <p className="whitespace-pre-line rounded-lg bg-nguy-nen px-3 py-2 text-xs text-nguy">{loi}</p>}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------- một hàng danh sách chờ */

/* ------------------------------------------------------------------- gỡ tủ */

/**
 * 🔴 GỠ TỦ KHỎI TUYẾN CHIẾU SÁNG — nghịch đảo của "Gán tuyến", cùng trang, cùng dữ liệu.
 *
 * Gọi `DELETE /api/cabinets/:id`, mà tuyến đó **không xoá hàng nào**: nó đặt `routeId = NULL`
 * và trả về `xoaHang: false`, `khungDaGuiXuongTu: 0`. Ba điều cấm YC-21 gặp nhau ở đúng đây:
 *
 *   1. ⛔ KHÔNG một gói tin nào xuống tủ — đèn ngoài hiện trường vẫn chạy theo lịch cũ và
 *      không được biết nó vừa bị gỡ trên app.
 *   2. ⛔ KHÔNG xoá hàng — lịch sử, số đo, cảnh báo giữ nguyên.
 *   3. Gỡ khỏi tài khoản là hệ quả: `RouteAccess` đi theo TUYẾN CHIẾU SÁNG, không đường thì
 *      không tài khoản nào với tới.
 *
 * ⚠ VÌ SAO Ở ĐÂY KHÔNG BẮT GÕ SỐ nhưng hộp thoại XOÁ TUYẾN CHIẾU SÁNG thì có:
 *   xoá tuyến chiếu sáng động tới N cái tủ mà người bấm **không nhìn thấy cái nào**; gỡ một tủ động
 *   tới đúng cái tủ có tên trong hộp thoại, và **nút hoàn tác nằm ngay dưới, cùng màn hình**.
 *   Bắt gõ số ở đây là làm phiền mà không mua thêm được gì.
 */
function HopThoaiGoTu({ tu, duong, onDong, onXong }: {
  tu: TreeCabinet; duong: RouteRow; onDong: () => void; onXong: (msg: string) => void;
}) {
  const [loi, setLoi] = useState<string | null>(null);
  const [ketQua, setKetQua] = useState<{ xoaHang: boolean; khungDaGuiXuongTu: number } | null>(null);

  const go = useMutation({
    mutationFn: () => api.del<{ xoaHang: boolean; khungDaGuiXuongTu: number }>(`/api/cabinets/${tu.id}`),
    onSuccess: (r) => {
      // Máy chủ nói ra hai con số; hiện chúng thay vì tin. Nếu một ngày nào đó chúng khác 0,
      // người bấm nút là người đầu tiên biết — chứ không phải cái tủ ngoài hiện trường.
      setKetQua(r);
      if (r.xoaHang === true || r.khungDaGuiXuongTu > 0) {
        setLoi('⛔ Máy chủ báo đã xoá hàng hoặc đã gửi gói tin xuống tủ. Báo ngay cho dev_app — '
             + 'đây là điều cấm 1/2 của YC-21.');
        return;
      }
      onXong(`Đã gỡ ${tu.code} khỏi tuyến chiếu sáng. Tìm lại ở khối "Danh sách chờ gán tuyến".`);
    },
    onError: (e) => setLoi(errorMessage(e)),
  });

  return (
    <Modal open title="Gỡ tủ khỏi tuyến chiếu sáng" onClose={onDong}
      footer={
        <>
          <Button variant="ghost" onClick={onDong}>Huỷ</Button>
          <Button variant="danger" loading={go.isPending}
                  onClick={() => { setLoi(null); go.mutate(); }}>Gỡ khỏi tuyến</Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p>
          Gỡ tủ <b>{tu.code} · {tu.name}</b> khỏi <b>{duong.tenDayDu}</b>?
        </p>
        <div className="rounded-lg bg-nen-2 px-3 py-2 text-xs text-chu-2">
          <ul className="list-disc space-y-1 pl-4">
            <li>Tủ <b>KHÔNG</b> bị xoá — hàng, lịch sử, số đo, cảnh báo giữ nguyên.</li>
            <li><b>KHÔNG</b> có gói tin nào gửi xuống tủ. Đèn vẫn chạy theo lịch cũ, tủ không biết gì.</li>
            <li>Tủ rời khỏi mọi tài khoản cho tới khi được gán tuyến lại.</li>
            <li>Tìm lại ngay ở khối <b>"Danh sách chờ gán tuyến"</b> phía dưới, và gán lại bằng một cú bấm.</li>
          </ul>
        </div>
        {ketQua && (
          <p className="rounded-lg bg-nen-2 px-3 py-2 font-mono text-xs text-chu-2">
            máy chủ trả: xoaHang={String(ketQua.xoaHang)} · khungDaGuiXuongTu={ketQua.khungDaGuiXuongTu}
          </p>
        )}
        {loi && <p className="whitespace-pre-line rounded-lg bg-nguy-nen px-3 py-2 text-xs text-nguy">{loi}</p>}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ gán tuyến */

function HopThoaiGan({ tu, duongs, onDong, onXong }: {
  tu: TuChoXuLy; duongs: RouteRow[]; onDong: () => void; onXong: (msg: string) => void;
}) {
  const [routeId, setRouteId] = useState('');
  const [loi, setLoi] = useState<string | null>(null);

  const gan = useMutation({
    mutationFn: () => api.patch<{ code: string | null }>(`/api/cabinets/${tu.id}`, { routeId }),
    // TK-17: mã do MÁY CHỦ cấp theo tuyến đích — nói ra mã vừa cấp, giao diện không tự đoán.
    onSuccess: (c) => onXong(`Đã gán ${tu.name} vào tuyến chiếu sáng — mã tủ ${c.code ?? '(chưa cấp)'}`),
    onError: (e) => setLoi(errorMessage(e)),
  });

  return (
    <Modal open title="Gán tủ vào tuyến chiếu sáng" onClose={onDong}
      footer={
        <>
          <Button variant="ghost" onClick={onDong}>Huỷ</Button>
          <Button variant="primary" loading={gan.isPending} disabled={!routeId}
                  onClick={() => { setLoi(null); gan.mutate(); }}>Gán</Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p>Tủ <b>{tu.name}</b> <span className="text-chu-2">({tu.hardwareId})</span></p>
        <Field label="Tuyến chiếu sáng" required>
          {/* 🔴 Ô CHỌN cũng phải là tên + địa chỉ. Danh sách toàn "Nguyễn Huệ" trơ trọi
              là danh sách không chọn được. */}
          <select className="inp" value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">— Chọn tuyến chiếu sáng —</option>
            {duongs.map((r) => <option key={r.id} value={r.id}>{r.tenDayDu}</option>)}
          </select>
        </Field>
        <p className="rounded-lg bg-nen-2 px-3 py-2 text-xs text-chu-2">
          Gán tuyến là việc của <b>máy chủ</b>. Không có gói tin nào gửi xuống tủ — tủ không có chỗ
          nào để chứa khái niệm "tuyến chiếu sáng". Mã tủ do máy chủ cấp: số nhỏ nhất còn trống
          trong tuyến đích.
        </p>
        {loi && <p className="whitespace-pre-line rounded-lg bg-nguy-nen px-3 py-2 text-xs text-nguy">{loi}</p>}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------- một hàng của danh sách chờ */

function HangCho({ tu, tick, onTick, onDatTen, onGanDuong }: {
  tu: TuChoXuLy; tick: boolean; onTick: (v: boolean) => void;
  onDatTen: () => void; onGanDuong: () => void;
}) {
  const daDatTen = tu.datTenLuc != null;
  return (
    <tr className={tick ? 'bg-nhan/10' : undefined}>
      <td>
        <input type="checkbox" className="h-4 w-4 accent-[rgb(var(--nhan))]"
               aria-label={`Chọn tủ ${tu.code ?? tu.hardwareId}`}
               checked={tick} onChange={(e) => onTick(e.target.checked)} />
      </td>
      {/*
        * ⛔ "ĐÃ ĐẶT TÊN" ĐỌC Ở `datTenLuc`, TUYỆT ĐỐI KHÔNG DÒ CHUỖI `AUTO-` (chú thích ở
        *   `types.ts` viết sẵn điều này). Một cái tên người dùng tự gõ là "AUTO-cổng 3" thì
        *   phép dò chuỗi sẽ bảo nó chưa được đặt tên, mãi mãi.
        */}
      <td>
        <span className="flex min-w-0 items-center gap-2">
          <Server className="h-4 w-4 shrink-0 text-chu-3" />
          <span className={`truncate ${daDatTen ? 'font-medium text-chu-1' : LOP_CHUA_RO}`}
                title={daDatTen ? tu.name : `${tu.name} — tủ tự khai, chưa ai đặt tên`}>
            {tu.name}
          </span>
        </span>
      </td>
      <td className="text-chu-2">
        <span className="so-do text-xs">{tu.code ?? '—'}</span>
        <span className="block truncate font-mono text-xs text-chu-3">{tu.hardwareId}</span>
      </td>
      {/*
        * 🔴 LUẬT SỐ 0: tủ CHƯA TỪNG lên mạng ≠ tủ MẤT KẾT NỐI. Cái đầu là một tủ chưa ai cắm
        *   xong; cái sau là một tủ đang hỏng. Vẽ chung là gửi người ta đi sai chỗ.
        */}
      <td className="text-chu-2">
        {tu.lastSeenAt ? fmtAgo(tu.lastSeenAt) : <span className="text-chu-3">chưa lên mạng lần nào</span>}
      </td>
      <td>
        {tu.lastSeenAt == null
          ? <Badge tone="slate">chưa lên mạng lần nào</Badge>
          : <Badge tone={tu.online ? 'green' : 'slate'}>
              <StatusDot online={tu.online} />{tu.online ? 'Đang nối' : 'Mất kết nối'}
            </Badge>}
      </td>
      {/* Cột "Hồ sơ" — máy chủ suy ra, giao diện KHÔNG đoán lại từ `name` hay `routeId`. */}
      <td>
        <Badge tone={tu.thieu === 'ca-hai' ? 'red' : 'amber'}>{NHAN_THIEU[tu.thieu]}</Badge>
      </td>
      <td>
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" icon={<Pencil className="h-3.5 w-3.5" />}
                  onClick={onDatTen}>Đặt tên</Button>
          {tu.routeId == null && (
            <Button size="sm" variant="ghost" onClick={onGanDuong}>Gán tuyến</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ đặt tên một tủ */

/**
 * ⚠ `GD-06` — ĐẶT TÊN TÁCH RA THÀNH HỘP THOẠI. Bản cũ sửa tên NGAY TRONG HÀNG, và trong một
 *   cái bảng có cột thì ô nhập tại chỗ làm cả hàng nhảy chiều cao, đẩy những hàng dưới đi.
 *   Cùng một việc, cùng một API — chỉ đổi chỗ đứng.
 */
function HopThoaiDatTen({ tu, onDong, onXong }: {
  tu: TuChoXuLy; onDong: () => void; onXong: (msg: string) => void;
}) {
  const [ten, setTen] = useState(tu.name);
  const [loi, setLoi] = useState<string | null>(null);
  const [trung, setTrung] = useState<Array<{ id: string; name: string }>>([]);

  const luu = useMutation({
    mutationFn: (t: string) =>
      api.patch<{ canhBaoTrungTen: Array<{ id: string; name: string }> }>(
        `/api/cabinets/${tu.id}`, { name: t }),
    onSuccess: (r) => {
      // ⚠ TRÙNG TÊN THÌ NHẮC, KHÔNG CHẶN: `name` là ghi chú, không phải danh tính. Nhưng
      // lúc đặt tên cả lô, trùng tên thường là GÕ NHẦM DÒNG — nhắc thì sửa được ngay.
      const ds = r.canhBaoTrungTen ?? [];
      setTrung(ds);
      if (ds.length === 0) onXong('Đã đặt tên tủ');
    },
    onError: (e) => setLoi(errorMessage(e)),
  });

  return (
    <Modal open title="Đặt tên tủ" onClose={onDong}
      footer={
        <>
          <Button variant="ghost" onClick={onDong}>{trung.length > 0 ? 'Đóng' : 'Huỷ'}</Button>
          <Button variant="primary" loading={luu.isPending} disabled={!ten.trim()}
                  onClick={() => { setLoi(null); setTrung([]); luu.mutate(ten.trim()); }}>Lưu</Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-chu-2">
          <span className="so-do">{tu.code ?? '—'}</span> · <span className="font-mono text-xs">{tu.hardwareId}</span>
        </p>
        <Field label="Tên tủ" hint="Một câu để người nhớ. Trùng tên là hợp lệ — danh tính là mã phần cứng.">
          <input autoFocus className="inp" value={ten} onChange={(e) => setTen(e.target.value)}
                 placeholder="vd: bàn test 3 · lô tháng 8, cái thứ 12"
                 onKeyDown={(e) => { if (e.key === 'Enter' && ten.trim()) { setLoi(null); luu.mutate(ten.trim()); } }} />
        </Field>
        {trung.length > 0 && (
          <p className="rounded bg-canh-nen px-2 py-1.5 text-xs text-canh">
            ⚠ Đã lưu. Còn {trung.length} tủ khác cũng tên này. Trùng tên là hợp lệ — tên tủ là
            ghi chú, không phải danh tính. Chỉ nhắc phòng khi bạn vừa gõ nhầm dòng.
          </p>
        )}
        {loi && <p className="rounded bg-nguy-nen px-2 py-1.5 text-xs text-nguy">{loi}</p>}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------- hàng loạt */

/** Kết quả MỘT dòng của một lần gửi hàng loạt. `loi === null` = dòng ấy đã xong. */
type KetQuaDong = { id: string; nhan: string; loi: string | null };

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * GÁN TUYẾN / ĐẶT TÊN HÀNG LOẠT — và điều quan trọng nhất của hộp này là nó KHÔNG NÓI DỐI
 * VỀ CÁCH NÓ LÀM VIỆC.
 *
 * 🔴 KHÔNG CÓ API HÀNG LOẠT (phiếu chốt: *"gửi từng tủ một qua API hiện có"*). Nên đây là N
 *   lời gọi `PATCH`, chạy TUẦN TỰ, và mỗi lời gọi có thể hỏng riêng. Hậu quả thật:
 *     · đứt mạng giữa chừng ⇒ một phần đã đổi trên máy chủ, một phần chưa;
 *     · KHÔNG có "hoàn tác cả lô" — vì chưa bao giờ có "cả lô".
 *   ⇒ Hộp này in ra TỪNG DÒNG một, và giữ tick cho những dòng hỏng để gửi lại đúng chúng.
 * ⛔ TUẦN TỰ chứ KHÔNG `Promise.all`: 50 lời gọi cùng lúc tới cùng một máy chủ là cách tự tạo
 *   ra lỗi rồi báo cáo nó như lỗi của người dùng. Chậm hơn, và đọc được.
 * ⚠ VÀ KHÔNG DỪNG Ở DÒNG HỎNG ĐẦU TIÊN: người dùng đã chọn 20 tủ; dừng ở tủ thứ 3 vì tủ ấy
 *   vừa bị ai đó xoá là bỏ rơi 17 tủ còn lại mà không có lý do gì.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function HopThoaiHangLoat({ viec, tus, duongs, onDong, onXong }: {
  viec: 'gan' | 'dat-ten';
  tus: TuChoXuLy[];
  duongs: RouteRow[];
  onDong: () => void;
  onXong: (conLoi: string[], msg: string) => void;
}) {
  const [routeId, setRouteId] = useState('');
  const [tienTo, setTienTo] = useState('');
  const [batDau, setBatDau] = useState(1);
  const [doRong, setDoRong] = useState(2);
  const [dangChay, setDangChay] = useState(false);
  const [ketQua, setKetQua] = useState<KetQuaDong[] | null>(null);

  /*
   * ⛔ TÊN DỰNG SẴN VÀ HIỆN RA TRƯỚC KHI GỬI. Một hộp "tiền tố + số thứ tự" mà không cho xem
   *   trước là một hộp người dùng phải ĐOÁN kết quả, và họ sẽ đoán sai về số 0 đệm đầu.
   * ⚠ THỨ TỰ ĐÁNH SỐ = thứ tự đang hiện trong bảng (`tus` đã theo đúng thứ tự ấy), không phải
   *   thứ tự trong CSDL. Người dùng đánh số theo cái họ NHÌN THẤY.
   */
  const tenMoi = (i: number) => `${tienTo}${String(batDau + i).padStart(doRong, '0')}`;
  const tuyenDaChon = duongs.find((r) => r.id === routeId) ?? null;
  const sanSang = viec === 'gan' ? routeId !== '' : tienTo.trim() !== '';

  const chay = async () => {
    setDangChay(true);
    const kq: KetQuaDong[] = [];
    for (let i = 0; i < tus.length; i += 1) {
      const t = tus[i];
      const than = viec === 'gan' ? { routeId } : { name: tenMoi(i) };
      const nhan = viec === 'gan' ? (t.code ?? t.hardwareId) : `${t.code ?? t.hardwareId} → ${tenMoi(i)}`;
      try {
        await api.patch(`/api/cabinets/${t.id}`, than);
        kq.push({ id: t.id, nhan, loi: null });
      } catch (e) {
        kq.push({ id: t.id, nhan, loi: errorMessage(e) });
      }
      setKetQua([...kq]);
    }
    setDangChay(false);
    const loi = kq.filter((x) => x.loi !== null);
    const xong = kq.length - loi.length;
    onXong(
      loi.map((x) => x.id),
      loi.length === 0
        ? `Đã ${viec === 'gan' ? 'gán' : 'đặt tên'} ${xong} tủ`
        : `${xong} tủ xong · ${loi.length} tủ lỗi — những dòng lỗi vẫn còn được chọn`,
    );
  };

  const dungTen = viec === 'gan' ? 'Gán vào tuyến' : 'Đặt tên hàng loạt';
  return (
    <Modal open title={`${dungTen} · ${tus.length} tủ`} onClose={dangChay ? () => {} : onDong}
      footer={
        <>
          <Button variant="ghost" disabled={dangChay} onClick={onDong}>
            {ketQua && !dangChay ? 'Đóng' : 'Huỷ'}
          </Button>
          <Button variant="primary" loading={dangChay} disabled={!sanSang || ketQua !== null}
                  onClick={() => void chay()}>
            Gửi {tus.length} lời gọi
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {viec === 'gan' ? (
          <Field label="Tuyến chiếu sáng" required>
            {/* 🔴 Ô CHỌN cũng phải là tên + địa chỉ (YC-21). Danh sách toàn "Nguyễn Huệ" trơ
                trọi là danh sách không chọn được — và ở đây nó quyết định số phận của N cái tủ. */}
            <select className="inp" value={routeId} disabled={dangChay}
                    onChange={(e) => setRouteId(e.target.value)}>
              <option value="">— Chọn tuyến chiếu sáng —</option>
              {duongs.map((r) => <option key={r.id} value={r.id}>{r.tenDayDu}</option>)}
            </select>
          </Field>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_88px_88px]">
            <Field label="Tiền tố" required>
              <input className="inp" value={tienTo} disabled={dangChay}
                     onChange={(e) => setTienTo(e.target.value)} placeholder="vd: NVL-T" />
            </Field>
            <Field label="Bắt đầu từ">
              <input className="inp" type="number" min={0} value={batDau} disabled={dangChay}
                     onChange={(e) => setBatDau(Number(e.target.value) || 0)} />
            </Field>
            <Field label="Số chữ số">
              <input className="inp" type="number" min={1} max={6} value={doRong} disabled={dangChay}
                     onChange={(e) => setDoRong(Math.min(6, Math.max(1, Number(e.target.value) || 1)))} />
            </Field>
          </div>
        )}

        {/*
          * ⛔ XEM TRƯỚC TỪNG DÒNG — đây là chỗ người dùng bắt được cái nhầm TRƯỚC khi nó thành
          *   N lời gọi không hoàn tác được.
          */}
        <div className="max-h-44 overflow-y-auto rounded-lg border border-vien">
          <ul className="divide-y divide-vien text-xs">
            {tus.map((t, i) => {
              const kq = ketQua?.find((x) => x.id === t.id) ?? null;
              return (
                <li key={t.id} className="flex items-center gap-2 px-2.5 py-1.5">
                  <span className="so-do shrink-0 text-chu-2">{t.code ?? t.hardwareId}</span>
                  <span className="truncate text-chu-3">
                    {viec === 'gan'
                      ? (tuyenDaChon ? `→ ${tuyenDaChon.tenDayDu}` : '→ chưa chọn tuyến')
                      : `→ ${tienTo ? tenMoi(i) : 'chưa có tiền tố'}`}
                  </span>
                  <span className="ml-auto shrink-0">
                    {kq === null
                      ? (dangChay ? <span className="text-chu-3">đang chờ…</span> : null)
                      : kq.loi === null
                        ? <Badge tone="green">xong</Badge>
                        : <Badge tone="red">lỗi</Badge>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* ⛔ LỜI LỖI IN NGUYÊN VĂN, từng dòng — một câu "có lỗi xảy ra" không sửa được gì. */}
        {ketQua?.some((x) => x.loi !== null) && (
          <ul className="space-y-1">
            {ketQua.filter((x) => x.loi !== null).map((x) => (
              <li key={x.id} className="rounded bg-nguy-nen px-2 py-1 text-xs text-nguy">
                <b>{x.nhan}</b> — {x.loi}
              </li>
            ))}
          </ul>
        )}

        <p className="rounded-lg bg-nen-2 px-3 py-2 text-xs text-chu-2">
          Không có lệnh hàng loạt ở máy chủ: đây là <b>{tus.length} lời gọi gửi lần lượt</b>. Đứt
          giữa chừng thì phần đã gửi vẫn có hiệu lực — bảng dưới nói rõ dòng nào xong, dòng nào
          chưa. <b>Không có gói tin nào xuống tủ</b>: gán tuyến và đặt tên đều là việc của máy chủ.
        </p>
      </div>
    </Modal>
  );
}

/* ======================================================== TK-17 · tủ chờ duyệt */

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-17 — KHỐI "TỦ CHỜ DUYỆT" (+ danh sách đã chặn). Chỉ `SUPER_ADMIN`.
 *
 * 🔴 Gốc ba lỗi lee báo 05/10: máy chủ coi MỌI tủ đăng ký được là tài sản. Nay tủ vừa REGISTER
 *   lần đầu nằm ở đây — không tuyến, không mã, không ở trang vận hành — cho tới khi có người
 *   DUYỆT (thành tài sản, xuống "Danh sách chờ gán tuyến") hoặc CHẶN (tủ test / không bán).
 *
 * ⚠ Là một KHỐI trong `/con-duong`, không phải màn hình mới — cùng tiền lệ "Danh sách chờ"
 *   (`Layout.tsx`: thêm màn hình đang bị chặn tới sau 0–G).
 * ⛔ Không nút nào ở đây gửi gì xuống tủ. Tủ chờ duyệt / bị chặn VẪN nối và VẪN chạy lịch —
 *   nên mỗi hàng có lối sang trang Lịch của chính tủ ấy.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function KhoiChoDuyet({ onDoi }: { onDoi: () => void }) {
  const cho = useChoDuyet();
  const chan = useBiChan();
  const toast = useToast();
  const [dangChan, setDangChan] = useState<TuChoDuyet | null>(null);
  const [moChan, setMoChan] = useState(false);
  /* TK-40 §4: duyệt ⇒ hỏi "Tủ mới" hay "Thay thế tủ…" (tủ mới nhận tuyến, tên, đèn, lịch… của tủ cũ; tủ cũ nghỉ hưu) */
  const [dangDuyet, setDangDuyet] = useState<TuChoDuyet | null>(null);
  const [thayThe, setThayThe] = useState<string>('');
  const tree = useTree();
  const tuCoThe = useMemo(() => (tree.data ?? []).flatMap((r) => r.cabinets.map((c) => ({ id: c.id, ten: `${r.tenDayDu} / ${c.name}`, matKetNoi: c.status !== 'ONLINE' })))
    .sort((a, b) => Number(b.matKetNoi) - Number(a.matKetNoi) || a.ten.localeCompare(b.ten, 'vi')), [tree.data]);

  const lam = useMutation({
    mutationFn: ({ tu, viec, thayTheId }: { tu: { id: string }; viec: 'duyet' | 'chan' | 'bo-chan'; thayTheId?: string }) =>
      api.post<TuChoDuyet>(`/api/cabinets/${tu.id}/${viec}`, thayTheId ? { thayTheId } : {}),
    onSuccess: (_c, { viec }) => {
      toast.success(viec === 'duyet'
        ? 'Đã duyệt — tủ chuyển xuống "Danh sách chờ gán tuyến" để đặt tên và gán tuyến.'
        : viec === 'chan'
          ? 'Đã chặn — tủ không hiện ở danh sách chờ nữa, kể cả khi nó nối lại. Tủ vẫn chạy lịch.'
          : 'Đã bỏ chặn — tủ về hàng chờ duyệt.');
      setDangChan(null);
      setDangDuyet(null); setThayThe('');
      onDoi();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const ds = cho.data ?? [];
  const dsChan = chan.data ?? [];
  return (
    <Card
      title="Tủ chờ duyệt"
      goiY={{ nhan: 'Tủ chờ duyệt', noiDung: 'Tủ vừa tự đăng ký lần đầu. Chưa là tài sản: không thuộc tuyến nào, không hiện ở trang vận hành — nhưng vẫn nối và vẫn chạy lịch.' }}
      actions={cho.data ? <Badge tone={ds.length ? 'amber' : 'slate'}>{ds.length} tủ</Badge> : null}
    >
      {cho.isLoading ? <Spinner />
        : cho.error ? <ErrorBox error={cho.error} onRetry={() => cho.refetch()} />
        : ds.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-8 w-8" />}
            title="Không có tủ nào chờ duyệt"
            hint="Board vừa nạp firmware và nối lần đầu sẽ hiện ở đây. Tủ đã chặn không hiện lại."
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl table-fixed">
              <colgroup>
                <col /><col className="w-36" /><col className="w-36" /><col className="w-32" /><col className="w-64" />
              </colgroup>
              <thead>
                <tr>
                  <th>Mã phần cứng</th>
                  <th>Firmware</th>
                  <th>Lần đầu thấy</th>
                  <th>Kết nối</th>
                  <th className="text-right">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {ds.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <span className="flex min-w-0 items-center gap-2">
                        <Server className="h-4 w-4 shrink-0 text-chu-3" />
                        <span className="truncate font-medium text-chu-1" title={t.hardwareId}>{t.hardwareId}</span>
                      </span>
                    </td>
                    <td className="truncate text-chu-2" title={t.firmwareVersion ?? ''}>
                      {t.firmwareVersion ?? <span className={LOP_CHUA_RO}>chưa khai</span>}
                    </td>
                    <td className="text-chu-2" title={fmtDateTime(t.createdAt)}>{fmtAgo(t.createdAt)}</td>
                    <td>
                      <Badge tone={t.online ? 'green' : 'slate'}>
                        <StatusDot online={t.online} />{t.online ? 'Đang nối' : 'Mất kết nối'}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <Link to={`/lich/${t.id}`} className="rounded-lg px-2 py-1 text-xs text-chu-2 hover:bg-nen-2">
                          Lịch
                        </Link>
                        <Button size="sm" variant="primary" icon={<ShieldCheck className="h-3.5 w-3.5" />}
                                loading={lam.isPending && lam.variables?.tu.id === t.id && lam.variables?.viec === 'duyet'}
                                onClick={() => setDangDuyet(t)}>
                          Duyệt
                        </Button>
                        <Button size="sm" variant="ghost" icon={<Ban className="h-3.5 w-3.5" />}
                                onClick={() => setDangChan(t)}>
                          Chặn
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Modal open={!!dangDuyet} onClose={() => { setDangDuyet(null); setThayThe(''); }} title={`Duyệt tủ ${dangDuyet?.hardwareId ?? ''}`}
        footer={<>
          <Button variant="secondary" onClick={() => { setDangDuyet(null); setThayThe(''); }}>Quay lại</Button>
          <Button variant="primary" loading={lam.isPending} disabled={thayThe === '-'} data-o="nut-duyet-xac-nhan"
            onClick={() => dangDuyet && lam.mutate({ tu: dangDuyet, viec: 'duyet', thayTheId: thayThe && thayThe !== '-' ? thayThe : undefined })}>Duyệt</Button>
        </>}>
        <div className="space-y-2 text-sm text-chu-1" data-o="hop-duyet">
          <label className="flex min-h-[36px] items-center gap-2"><input type="radio" name="kieu-duyet" className="h-4 w-4 accent-nhan" checked={thayThe === ''} onChange={() => setThayThe('')} />Tủ mới</label>
          <label className="flex min-h-[36px] items-center gap-2"><input type="radio" name="kieu-duyet" className="h-4 w-4 accent-nhan" checked={thayThe !== ''} onChange={() => setThayThe('-')} />Thay thế tủ…</label>
          {thayThe !== '' && (
            <select className="inp w-full" value={thayThe} onChange={(e) => setThayThe(e.target.value)} aria-label="Tủ được thay">
              <option value="-">— Chọn tủ được thay —</option>
              {tuCoThe.map((c) => <option key={c.id} value={c.id}>{c.ten}{c.matKetNoi ? ' · mất kết nối' : ''}</option>)}
            </select>
          )}
        </div>
      </Modal>

      {/* Đã chặn — gập sẵn: đây là trí nhớ của hệ thống, không phải việc phải làm hằng ngày. */}
      <div className="mt-3 border-t border-vien pt-3">
        <button type="button" onClick={() => setMoChan((v) => !v)}
                className="flex items-center gap-1.5 text-sm text-chu-2 hover:text-chu-1">
          {moChan ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Tủ đã chặn
          <span className="text-chu-3">{chan.data ? dsChan.length : '…'}</span>
        </button>
        {moChan && (
          chan.isLoading ? <Spinner />
            : chan.error ? <ErrorBox error={chan.error} onRetry={() => chan.refetch()} />
            : dsChan.length === 0 ? <p className="mt-2 text-sm text-chu-3">Chưa chặn tủ nào.</p>
            : (
              <div className="tbl-wrap mt-2">
                <table className="tbl table-fixed">
                  <colgroup><col /><col className="w-40" /><col /><col className="w-32" /></colgroup>
                  <thead>
                    <tr><th>Mã phần cứng</th><th>Chặn lúc</th><th>Lý do</th><th className="text-right">Hành động</th></tr>
                  </thead>
                  <tbody>
                    {dsChan.map((x) => (
                      <tr key={x.hardwareId}>
                        <td className="truncate font-medium text-chu-1" title={x.hardwareId}>{x.hardwareId}</td>
                        <td className="text-chu-2">{fmtDateTime(x.chanLuc)}</td>
                        <td className="truncate text-chu-2" title={x.lyDo ?? ''}>{x.lyDo ?? '—'}</td>
                        <td className="text-right">
                          {x.tu
                            ? <Button size="sm" variant="ghost" loading={lam.isPending && lam.variables?.tu.id === x.tu.id}
                                      onClick={() => lam.mutate({ tu: x.tu!, viec: 'bo-chan' })}>Bỏ chặn</Button>
                            : <span className="text-xs text-chu-3">không còn hàng tủ</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}
      </div>

      <ConfirmDialog
        open={dangChan !== null}
        title="Chặn tủ"
        confirmText="Chặn"
        loading={lam.isPending}
        message={dangChan && (
          <div className="space-y-2">
            <p>Chặn tủ <b>{dangChan.hardwareId}</b>?</p>
            <p className="text-chu-2">
              Máy chủ NHỚ mã phần cứng này: tủ nối lại bao nhiêu lần cũng không hiện ở danh sách chờ.
              Tủ vẫn chạy lịch đóng cắt — chặn không gửi gì xuống tủ. Bỏ chặn được ở mục "Tủ đã chặn".
            </p>
          </div>
        )}
        onConfirm={() => dangChan && lam.mutate({ tu: dangChan, viec: 'chan' })}
        onCancel={() => setDangChan(null)}
      />
    </Card>
  );
}
